import React, { useEffect } from 'react';

const TylerStateSummary: React.FC = () => {
  // Prevent Google indexing - hidden reference page
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <h1 style={{ borderBottom: '2px solid #e74c3c', paddingBottom: '10px' }}>TylerState - Simple Global State</h1>

      <div style={{ background: '#fff3cd', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <strong>Note:</strong> This is NOT the xstate library (state machines). It's just a plain JavaScript object we named "TylerState" for "cross-component state."
      </div>

      <h2>What It Is</h2>
      <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '20px', borderRadius: '8px', overflow: 'auto' }}>
{`// It's literally just an object
const TylerState = {
  car: { carA: { outputs: null }, carB: { outputs: null } },
  housing: { results: null },
  user: { isLoggedIn: false }
};`}
      </pre>
      <p>That's it. A global object that any component can read from or write to.</p>

      <h2>How We Use It</h2>

      <h3>Writing (in a calculator):</h3>
      <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '20px', borderRadius: '8px', overflow: 'auto' }}>
{`// After calculating results, store them
TylerState.car.carA.outputs = calculatedResults;
updateReact();  // Tells React to re-render`}
      </pre>

      <h3>Reading (in any component):</h3>
      <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '20px', borderRadius: '8px', overflow: 'auto' }}>
{`const MyResultsComponent = () => {
  useTylerStateUpdate();  // Hook that listens for updates

  const results = TylerState.car.carA.outputs;
  return <div>{results?.totalCost}</div>;
};`}
      </pre>

      <h2>Why It's Simpler Than Redux</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr style={{ background: '#f8f9fa' }}>
            <th style={{ border: '1px solid #dee2e6', padding: '12px', textAlign: 'left' }}>Redux</th>
            <th style={{ border: '1px solid #dee2e6', padding: '12px', textAlign: 'left' }}>Our TylerState</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>Actions, reducers, dispatch, selectors</td>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>Just read/write an object</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>50+ lines of boilerplate per feature</td>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>0 boilerplate</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}><code>dispatch(setCarResults(data))</code></td>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}><code>TylerState.car.results = data</code></td>
          </tr>
          <tr>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}><code>useSelector(state =&gt; state.car.results)</code></td>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}><code>TylerState.car.results</code></td>
          </tr>
          <tr>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>Immutable updates required</td>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>Mutate directly</td>
          </tr>
          <tr>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>DevTools, middleware, thunks</td>
            <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>Console.log if needed</td>
          </tr>
        </tbody>
      </table>
      <p>Redux is designed for huge teams and complex apps. For a small/medium app with one developer, it's overkill.</p>

      <h2>When to Use TylerState</h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div style={{ background: '#d4edda', padding: '15px', borderRadius: '8px' }}>
          <strong style={{ color: '#155724' }}>Use for:</strong>
          <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
            <li>Calculator results that multiple components need</li>
            <li>User auth state (logged in, user info)</li>
            <li>UI flags shared across pages (which tab is active)</li>
          </ul>
        </div>
        <div style={{ background: '#f8d7da', padding: '15px', borderRadius: '8px' }}>
          <strong style={{ color: '#721c24' }}>Don't use for:</strong>
          <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
            <li>Form inputs (use local <code>useState</code>)</li>
            <li>Modal open/close state</li>
            <li>Anything only one component needs</li>
          </ul>
        </div>
      </div>

      <h2>The Magic: updateReact()</h2>
      <p>The one trick is telling React "hey, the data changed":</p>
      <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '20px', borderRadius: '8px', overflow: 'auto' }}>
{`// In TylerState.ts
let listeners: Function[] = [];

export const updateReact = () => {
  listeners.forEach(fn => fn());
};

export const useTylerStateUpdate = () => {
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const update = () => forceUpdate(n => n + 1);
    listeners.push(update);
    return () => {
      listeners = listeners.filter(l => l !== update);
    };
  }, []);
};`}
      </pre>
      <p>Components that call <code>useTylerStateUpdate()</code> will re-render when anyone calls <code>updateReact()</code>.</p>

      <h2 style={{ borderTop: '2px solid #e74c3c', paddingTop: '20px', marginTop: '30px' }}>TL;DR</h2>
      <ul style={{ fontSize: '1.1em', lineHeight: '1.8' }}>
        <li>It's a global object + a way to trigger re-renders</li>
        <li>No boilerplate, no actions, no reducers</li>
        <li>Write: <code>TylerState.thing = value; updateReact();</code></li>
        <li>Read: <code>useTylerStateUpdate(); return TylerState.thing;</code></li>
        <li>Use Redux when you have 10+ developers. Use this when it's just you.</li>
      </ul>

      <div style={{ marginTop: '40px', padding: '20px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
        <p style={{ margin: 0, color: '#6c757d' }}>
          From Larry's Help - larryshelp.org
        </p>
      </div>
    </div>
  );
};

export default TylerStateSummary;
